import { useState, useEffect } from 'react';
import { translations } from "@/translations/translations";
import kevinPhoto from "@/assets/kevin_2.jpg";
import { getRandomFacts } from "@/data/author/funFacts";

interface AboutAuthorProps {
    language: 'en' | 'de';
}

const AboutAuthor = ({ language }: AboutAuthorProps) => {
    const [showFunFacts, setShowFunFacts] = useState(false);
    const [funFacts, setFunFacts] = useState<{ text: string; isLie: boolean }[]>([]);
    const t = translations[language];

    // Load random facts when component mounts or language changes
    useEffect(() => {
        setFunFacts(getRandomFacts(language));
    }, [language]);

    const toggleFunFacts = () => {
        setShowFunFacts(!showFunFacts);
        // Get new random facts when toggling to fun facts
        if (!showFunFacts) {
            setFunFacts(getRandomFacts(language));
        }
    };

    return (
        <div className="mt-12 pt-8 border-t border-border">
            {/* Mobile layout: stacked */}
            <div className="flex flex-col sm:hidden gap-4">
                <div className="flex flex-row items-start gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-4">
                            <h2 className="text-2xl font-bold">{t["blog.author.heading"]}</h2>
                            <button
                                onClick={toggleFunFacts}
                                className="text-sm px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary font-medium rounded-md transition-colors whitespace-nowrap self-start"
                            >
                                {showFunFacts ? t["blog.author.tooPlayful"] : t["blog.author.tooSerious"]}
                            </button>
                        </div>
                    </div>
                    <div className="flex-shrink-0 w-28 h-28">
                        <img
                            // SEO: Added Heilbronn keywords for local OD searches [Google guideline]
                            src={kevinPhoto}
                            alt="Kevin Rassner - Systemic Organizational Developer and Agile COO Coach in Heilbronn"
                            className="w-full h-full rounded-lg object-cover border-2 border-primary/20"
                        />
                    </div>
                </div>
                <div className="w-full">
                    {!showFunFacts ? (
                        <p className="text-foreground/90 leading-relaxed">
                            {t["blog.author.bio"]}
                        </p>
                    ) : (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-foreground/90">
                                {t["blog.author.truthsAndLie.heading"]}
                            </h3>
                            <ul className="space-y-3">
                                {funFacts.map((fact, index) => (
                                    <li key={index} className="flex items-start">
                                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary font-medium text-sm mr-3 flex-shrink-0">
                                            {index + 1}
                                        </span>
                                        <span className="text-foreground/90">
                                            {fact.text}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-sm text-foreground/60 italic">
                                {language === 'en'
                                    ? "Can you spot the lie?"
                                    : "Können Sie die Lüge erkennen?"}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop layout: image on right, all content on left */}
            <div className="hidden sm:flex flex-row gap-6">
                <div className="flex-1 flex flex-col gap-4">
                    <div className="flex flex-row items-center gap-4">
                        <h2 className="text-2xl font-bold">{t["blog.author.heading"]}</h2>
                        <button
                            onClick={toggleFunFacts}
                            className="text-sm px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary font-medium rounded-md transition-colors whitespace-nowrap"
                        >
                            {showFunFacts ? t["blog.author.tooPlayful"] : t["blog.author.tooSerious"]}
                        </button>
                    </div>
                    <div className="w-full">
                        {!showFunFacts ? (
                            <p className="text-foreground/90 leading-relaxed">
                                {t["blog.author.bio"]}
                            </p>
                        ) : (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-foreground/90">
                                    {t["blog.author.truthsAndLie.heading"]}
                                </h3>
                                <ul className="space-y-3">
                                    {funFacts.map((fact, index) => (
                                        <li key={index} className="flex items-start">
                                            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary font-medium text-sm mr-3 flex-shrink-0">
                                                {index + 1}
                                            </span>
                                            <span className="text-foreground/90">
                                                {fact.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-sm text-foreground/60 italic">
                                    {language === 'en'
                                        ? "Can you spot the lie?"
                                        : "Können Sie die Lüge erkennen?"}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-shrink-0 w-64">
                    <img
                        // SEO: Added Heilbronn keywords for local OD searches [Google guideline]
                            src={kevinPhoto}
                        alt="Kevin Rassner - Systemic Organizational Developer and Agile COO Coach in Heilbronn"
                        className="w-full h-full rounded-lg object-cover border-2 border-primary/20"
                    />
                </div>
            </div>
        </div>
    );
};

export default AboutAuthor;
